import { useRef, useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import PhotoUploader from "@/components/PhotoUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Eraser } from "lucide-react";
import { toast } from "sonner";

const DEFECTS = ["Rayures", "Impacts", "Jantes abîmées", "Intérieur sali", "Objets laissés", "Autre"];

export function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (c && value && !c._loaded) {
      const img = new Image();
      img.onload = () => c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      img.src = value; c._loaded = true;
    }
  }, [value]);

  const pos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a"; ctx.lineTo(x, y); ctx.stroke(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(canvasRef.current.toDataURL("image/png")); };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(null); };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs text-slate-500">Signature client</Label>
        <button type="button" onClick={clear} className="text-[11px] text-slate-500 hover:text-red-600 flex items-center gap-1" data-testid="inspection-signature-clear"><Eraser className="w-3 h-3" /> Effacer</button>
      </div>
      <canvas ref={canvasRef} width={500} height={160} data-testid="inspection-signature-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full h-40 rounded-lg border border-slate-300 bg-white touch-none" />
    </div>
  );
}

export default function InspectionDialog({ open, setOpen, appointmentId, vehicleId, clientId, onSaved }) {
  const empty = { mileage: "", fuel_level: "", general_state: "", defects: [], notes: "", photos: [], signature: null };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(empty); }, [open]); // eslint-disable-line

  const toggleDefect = (d) => setForm((f) => ({ ...f, defects: f.defects.includes(d) ? f.defects.filter((x) => x !== d) : [...f.defects, d] }));

  const save = async () => {
    if (!vehicleId) return toast.error("Associez un véhicule au rendez-vous d'abord.");
    setSaving(true);
    try {
      await api.post("/inspections", {
        appointment_id: appointmentId || null, vehicle_id: vehicleId, client_id: clientId || null,
        mileage: form.mileage || null, fuel_level: form.fuel_level || null, general_state: form.general_state || null,
        defects: form.defects, notes: form.notes || null, photos: form.photos, signature: form.signature,
      });
      toast.success("État des lieux enregistré");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inspection-dialog">
        <DialogHeader><DialogTitle className="text-slate-900">État des lieux du véhicule</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Kilométrage</Label><Input data-testid="inspection-mileage" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Niveau carburant</Label><Input data-testid="inspection-fuel" value={form.fuel_level} onChange={(e) => setForm({ ...form, fuel_level: e.target.value })} placeholder="Ex : 1/2" className="bg-slate-50 border-slate-200" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">État général</Label><Input data-testid="inspection-general" value={form.general_state} onChange={(e) => setForm({ ...form, general_state: e.target.value })} placeholder="Ex : Bon état" className="bg-slate-50 border-slate-200" /></div>
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Défauts constatés</Label>
            <div className="flex flex-wrap gap-2">
              {DEFECTS.map((d) => (
                <button key={d} type="button" data-testid={`inspection-defect-${d}`} onClick={() => toggleDefect(d)}
                  className={`px-3 py-1.5 rounded-lg border text-xs transition ${form.defects.includes(d) ? "bg-amber-500 text-black border-amber-500 font-semibold" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{d}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Remarques</Label><Textarea data-testid="inspection-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
          <PhotoUploader label="Photos état des lieux" value={form.photos} onChange={(p) => setForm({ ...form, photos: p })} testid="inspection-photo" />
          <SignaturePad value={form.signature} onChange={(v) => setForm({ ...form, signature: v })} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="btn-save-inspection" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
